import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { useDemoMode } from "@/pages/demo/DemoModeContext";

export interface ItemBeneficiary {
  id: number;
  contact_id: number;
  contact_name: string;
  relationship: string | null;
  share_percentage: number | null;
  numerator: number | null;
  denominator: number | null;
  is_contingent: boolean;
}

export function useItemBeneficiaries(itemId: number | undefined) {
  const { isDemoMode } = useDemoMode();

  return useQuery({
    queryKey: ["item-beneficiaries", itemId],
    queryFn: async (): Promise<ItemBeneficiary[]> => {
      const { data, error } = await supabase
        .from("estate_item_beneficiaries")
        .select(
          `id, contact_id, share_percentage, numerator, denominator, is_contingent,
           estate_contacts!estate_item_beneficiaries_contact_id_fkey(first_name, last_name, relationship)`,
        )
        .eq("estate_item_id", itemId!)
        .eq("is_hidden", false);

      if (error) throw error;

      return (data ?? []).map((row) => {
        const contact = Array.isArray(row.estate_contacts)
          ? row.estate_contacts[0]
          : row.estate_contacts;
        const name = [contact?.first_name, contact?.last_name].filter(Boolean).join(" ");
        return {
          id: row.id,
          contact_id: row.contact_id,
          contact_name: name || "Unknown",
          relationship: (contact?.relationship as string) ?? null,
          share_percentage: row.share_percentage,
          numerator: row.numerator,
          denominator: row.denominator,
          is_contingent: row.is_contingent,
        };
      });
    },
    enabled: itemId !== undefined && !isDemoMode,
    staleTime: isDemoMode ? Infinity : 60_000,
  });
}
